import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getBlogPostsByCategory } from '../../firebase/models/blogService';

const SidebarContainer = styled.div`
  @media (max-width: 768px) {
    margin-top: var(--spacing-lg);
  }
`;

const SidebarSection = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h3`
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  font-size: 1.25rem;
  padding-bottom: var(--spacing-sm);
  border-bottom: 2px solid #f0f0f0;
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CategoryItem = styled.li`
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CategoryLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  justify-content: space-between;
  
  &:hover {
    color: var(--accent-blue);
  }
`;

const PostList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PostItem = styled.li`
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const PostLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  
  &:hover {
    color: var(--accent-blue);
  }
`;

const PostDate = styled.span`
  font-size: 0.85rem;
  color: #666;
  display: block;
  margin-top: 4px;
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormInput = styled.input`
  padding: var(--spacing-md);
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const SubmitButton = styled.button`
  padding: var(--spacing-md);
  background-color: var(--accent-blue);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: var(--dark-blue);
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  padding: var(--spacing-md);
  margin-top: var(--spacing-sm);
  background-color: ${props => props.error ? '#ffeeee' : '#eeffee'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const BlogSidebar = ({ recentPosts, categories }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [categoriesWithCounts, setCategoriesWithCounts] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Fetch actual post counts for categories
  useEffect(() => {
    const fetchCategoryCounts = async () => {
      if (!categories || categories.length === 0) {
        setCategoriesWithCounts([]);
        setLoadingCategories(false);
        return;
      }
      
      try {
        setLoadingCategories(true);
        
        // Create an array of promises to fetch posts for each category
        const countPromises = categories.map(async (category) => {
          const posts = await getBlogPostsByCategory(category.id);
          const publishedPosts = posts.filter(post => post.published);
          return {
            ...category,
            count: publishedPosts.length
          };
        });
        
        // Wait for all promises to resolve
        const categoriesWithActualCounts = await Promise.all(countPromises);
        
        // Filter out categories with zero posts
        const nonEmptyCategories = categoriesWithActualCounts.filter(cat => cat.count > 0);
        
        setCategoriesWithCounts(nonEmptyCategories);
      } catch (err) {
        console.error('Error fetching category counts:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    fetchCategoryCounts();
  }, [categories]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Simple email validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }
    
    try {
      // Here you would typically call an API to subscribe the user
      // For now, just simulate a successful subscription
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess('Thanks for subscribing to our newsletter!');
      setEmail('');
    } catch (err) {
      console.error('Newsletter signup error:', err);
      setError('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <SidebarContainer>
      <SidebarSection>
        <SectionTitle>Categories</SectionTitle>
        {loadingCategories ? (
          <p>Loading categories...</p>
        ) : categoriesWithCounts && categoriesWithCounts.length > 0 ? (
          <CategoryList>
            {categoriesWithCounts.map(category => (
              <CategoryItem key={category.id}>
                <CategoryLink to={`/blog/category/${category.slug || category.id}`}>
                  {category.name}
                  <span>{category.count}</span>
                </CategoryLink>
              </CategoryItem>
            ))}
          </CategoryList>
        ) : (
          <p>No categories found.</p>
        )}
      </SidebarSection>
      
      <SidebarSection>
        <SectionTitle>Recent Posts</SectionTitle>
        {recentPosts && recentPosts.length > 0 ? (
          <PostList>
            {recentPosts.map(post => (
              <PostItem key={post.id}>
                <PostLink to={`/blog/${post.slug}`}>
                  {post.title}
                  <PostDate>{formatDate(post.createdAt)}</PostDate>
                </PostLink>
              </PostItem>
            ))}
          </PostList>
        ) : (
          <p>No recent posts found.</p>
        )}
      </SidebarSection>
      
      <SidebarSection>
        <SectionTitle>Subscribe to Our Newsletter</SectionTitle>
        <div className="klaviyo-form-Y89cgu"></div>
      </SidebarSection>
    </SidebarContainer>
  );
};

export default BlogSidebar; 